// controllers/otp_controller.js
import { Controller } from "@hotwired/stimulus";
import { handleDetectPasskeyMetadata } from "../utils/browserDetectionHandler";

export default class OtpController extends Controller {
  static targets = ["form", "otpInput", "continueButton", "timer", "resendButton"];
  static values = {
    verifyurl: String,
    resendurl: String,
    email: String,
    duration: { type: Number, default: 300 },
    autoSubmit: { type: Boolean, default: true },
  };

  connect() {
    if (this.hasFormTarget) {
      this.formTarget.addEventListener("turbo:submit-end", this.onSubmitEnd);
    }
    this._autoSubmitting = false;

    this.otpInputTargets.forEach((el, idx) => {
      el.addEventListener("input", this.onOtpInput);
      el.addEventListener("keydown", this.onOtpBackspace);
      if (idx === 0) el.addEventListener("paste", this.onPasteCode);
      el.addEventListener("keydown", (e) => {
        if (e.key === "Enter") this.verifyAndSubmit(e);
      });
    });

    this.updateContinueDisabled();
    this.startTimer();
  }

  disconnect() {
    if (this.hasFormTarget) {
      this.formTarget.removeEventListener("turbo:submit-end", this.onSubmitEnd);
    }
    this.otpInputTargets.forEach((el, idx) => {
      el.removeEventListener("input", this.onOtpInput);
      el.removeEventListener("keydown", this.onOtpBackspace);
      if (idx === 0) el.removeEventListener("paste", this.onPasteCode);
    });
    clearInterval(this._timerId);
  }

  // Manual submit (also used by auto-submit)
  verifyAndSubmit = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (this._autoSubmitting) return;
    if (this._timeLeft <= 0) return;

    const code = this.otpInputTargets.map((i) => i.value.trim()).join("");
    if (!/^\d{6}$/.test(code)) {
      this.bumpInvalid();
      return;
    }

    this._autoSubmitting = true; // guard
    this.setHidden("code", code);
    if (this.hasEmailValue) this.setHidden("email", this.emailValue);
    if (this._timeLeft) this.setHidden("expiresTime", this._timeLeft);
    let metadata = handleDetectPasskeyMetadata();
    metadata.appType = "rocket";
    this.setHidden("metadata", JSON.stringify(metadata));
    if (this.hasVerifyurlValue) this.formTarget.action = this.verifyurlValue;
    this.formTarget.method = "post";
    this.formTarget.requestSubmit(); // Turbo events fire; lock/spinner controller will engage

  };

  // Auto-submit when all boxes filled
  maybeAutoSubmit() {
    if (!this.autoSubmitValue) return;
    const allFilled = this.otpInputTargets.every((i) => i.value.length === 1);
    if (allFilled) this.verifyAndSubmit(); // no event object needed
  }

  // Only purpose now: release guard so user can retry
  onSubmitEnd = (event) => {
    this._autoSubmitting = false;
  
    const { detail } = event;
    const { fetchResponse } = detail;
  
    if (fetchResponse) {
      if (fetchResponse.failed) {
        this.clearOtpInputs();
      }
    }
  };

  // Resend flow
  resend = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (this.hasEmailValue) this.setHidden("email", this.emailValue);
    this.setHidden("resend", true);

    if (this.hasResendurlValue) this.formTarget.action = this.resendurlValue;
    this.formTarget.method = "post";
    this.formTarget.requestSubmit();

    this.clearOtpInputs();
    this.updateContinueDisabled();
    this.startTimer();
  };

  // Timer (MM:SS)
  startTimer() {
    clearInterval(this._timerId);
    this._timeLeft = this.durationValue;

    const tick = () => {
      if (!this.hasTimerTarget) return;
      const m = Math.floor(this._timeLeft / 60);
      const s = String(this._timeLeft % 60).padStart(2, "0");
      this.timerTarget.textContent = this._timeLeft > 0 ? `Expires in ${m}:${s}` : "Expired";

      if (this._timeLeft <= 0) {
        clearInterval(this._timerId);
        if (this.hasContinueButtonTarget) this.continueButtonTarget.disabled = true;
        if (this.hasResendButtonTarget) this.resendButtonTarget.disabled = false;
        return;
      }
      this._timeLeft -= 1;
    };

    if (this.hasResendButtonTarget) this.resendButtonTarget.disabled = true;
    tick();
    this._timerId = setInterval(tick, 1000);
  }

  // OTP UX helpers
  onOtpInput = (e) => {
    const val = e.target.value;
    const numberRegex = /^\d$/;
    const digits = val.replace(/\D/g, "");

    if (digits.length > 1) {
      this.otpInputTargets.forEach((otpTarget, index) => {
        otpTarget.value = digits[index] || "";
      });

      if (digits.length === 6) {
        this.verifyAndSubmit();
      }
      return;
    }

    if (!/^\d$/.test(val)) {
      e.target.value = "";
      return;
    }

    if (e.target.nextElementSibling) {
      e.target.nextElementSibling.focus();
    }

    const allDigitsEntered = this.otpInputTargets.every(
      (code) => code.value.length === 1
    );
    if (allDigitsEntered) {
      this.verifyAndSubmit();
    }

    this.updateContinueDisabled();
  };

  onOtpBackspace = (e) => {
    if (e.key === "Backspace" && e.target.value === "") {
      const prev = e.target.previousElementSibling;
      if (prev && this.otpInputTargets.includes(prev)) {
        e.preventDefault();
        prev.value = "";
        prev.focus();
      }
      this.updateContinueDisabled();
    }
  };

  onPasteCode = (e) => {
    const text = (e.clipboardData || window.clipboardData).getData("text");
    const code = (text || "").replace(/\D/g, "").slice(0, 6);
    if (!code) return;

    e.preventDefault();
    this.otpInputTargets.forEach((box, i) => (box.value = code[i] || ""));
    const lastFilled = this.otpInputTargets[Math.min(code.length - 1, this.otpInputTargets.length - 1)];
    if (lastFilled) lastFilled.focus();

    this.updateContinueDisabled();
    this.maybeAutoSubmit();
  };

  updateContinueDisabled() {
    const allFilled = this.otpInputTargets.every((i) => i.value.length === 1);
    if (this.hasContinueButtonTarget) this.continueButtonTarget.disabled = !allFilled;
  }

  clearOtpInputs() {
    this.otpInputTargets.forEach((i) => (i.value = ""));
    if (this.otpInputTargets[0]) this.otpInputTargets[0].focus();
  }

  bumpInvalid() {
    this.otpInputTargets.forEach((i) => i.classList.add("ring-2", "ring-red-500"));
    setTimeout(() => this.otpInputTargets.forEach((i) => i.classList.remove("ring-2", "ring-red-500")), 350);
  }

  setHidden(name, value) {
    let el = this.formTarget.querySelector(`input[type="hidden"][name="${name}"]`);
    if (!el) {
      el = document.createElement("input");
      el.type = "hidden";
      el.name = name;
      this.formTarget.appendChild(el);
    }
    el.value = value;
  }
}
